import { isEmpty } from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { AddCircleIcon, DeleteIcon } from 'tdesign-icons-react';
import { Dialog, Form, Input, InputNumber, Link, MessagePlugin, Textarea } from 'tdesign-react';
import formRulePattern from '@/utils/formRulePattern';
import { createProxy, updateProxy, validateProxy } from '@/services/resourceService';
import Tip from '@/components/Tip';
import { isVoid } from '@/utils/common';
import ValidateWrapper from '@/components/Form/ValidateWrapper';

const { FormItem, FormList } = Form;

export default function FormModal({ visible, handleClose, detail }) {
  const isEdit = !isEmpty(detail);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  // 规避setFieldsValue bug
  const initialData = useMemo(() => {
    if (isEmpty(detail)) {
      return {
        path: [{ host: null, port: null }],
      };
    } else {
      return {
        ...detail,
        key: detail.proxyKey,
        name: detail.proxyName,
        pathPrefix: detail.proxyPath,
        path: detail.urls?.map((item) => {
          const [host, port] = item.split(':');
          return {
            host,
            port: Number(port),
          };
        }),
      };
    }
  }, [detail]);

  const handleSubmit = async ({ validateResult, fields }) => {
    if (validateResult === true) {
      try {
        setSubmitting(true);
        if (isEdit) {
          await updateProxy({
            ...fields,
            id: detail.id,
          });
        } else {
          await createProxy(fields);
        }
        MessagePlugin.success('操作成功');
        handleClose(true);
      } catch (error) {
        console.trace(error);
        throw error;
      } finally {
        setSubmitting(false);
      }
    }
  };
  return (
    <Dialog
      visible={visible}
      destroyOnClose
      onClose={() => handleClose()}
      header={isEdit ? '修改代理' : '新建代理'}
      width={700}
      confirmBtn={{
        loading: submitting,
        content: '确认',
      }}
      onConfirm={() => form.submit()}
    >
      <Form
        form={form}
        resetType='initial'
        onSubmit={handleSubmit}
        initialData={initialData}
        labelWidth={140}
        style={{ width: '86%' }}
      >
        <FormItem
          name='name'
          label={
            <Tip align='right' content='用于页面显示的代理名称'>
              代理名称
            </Tip>
          }
          rules={[{ required: true, message: '代理名称必填' }]}
        >
          <Input />
        </FormItem>
        <FormItem
          name='key'
          label={
            <Tip align='right' content='用于系统使用代理名称，英文字母+下划线组成'>
              唯一标识符
            </Tip>
          }
          rules={[{ required: true, message: '唯一标识符必填' }, formRulePattern('name')]}
        >
          <Input />
        </FormItem>
        <FormItem
          name='pathPrefix'
          label={
            <Tip
              align='right'
              content='可选项，请求uri前缀，用于适配请求转发环境，例如nginx的前缀匹配'
            >
              代理地址前缀
            </Tip>
          }
        >
          <Input />
        </FormItem>
        <FormItem
          label={
            <Tip align='right' content='部署的实际代理地址'>
              代理地址
            </Tip>
          }
          rules={[{ required: true }]}
        >
          <FormList name='path'>
            {(fields, { add, remove }) => (
              <ListContainer>
                {fields.map(({ key, name, ...restField }) => (
                  <ListItem key={key}>
                    <Left>
                      <FormItem
                        style={{ flex: 10, width: 0 }}
                        {...restField}
                        name={[name, 'host']}
                        rules={[{ required: true }]}
                      >
                        <Input placeholder='请输入host' />
                      </FormItem>
                      <span style={{ lineHeight: '32px' }}>&nbsp;-&nbsp;</span>
                      <FormItem
                        style={{ flex: 7, width: 0, paddingRight: 6 }}
                        {...restField}
                        name={[name, 'port']}
                        rules={[{ required: true }]}
                      >
                        <ValidateWrapper
                          onValidate={async () => {
                            const { host, port } = form.getFieldValue(['path'])[name];
                            if (isVoid(host)) {
                              MessagePlugin.warning('请先输入');
                              return;
                            }
                            const { data } = await validateProxy({ url: `${host}:${port}` });
                            return data;
                          }}
                        >
                          <InputNumber theme='normal' decimalPlaces={0} placeholder='端口号' />
                        </ValidateWrapper>
                      </FormItem>
                    </Left>

                    {fields.length > 1 && (
                      <FormItem style={{ marginLeft: 8 }}>
                        <DeleteIcon
                          size='16px'
                          style={{ cursor: 'pointer', color: 'var(--td-text-color-secondary)' }}
                          onClick={() => remove(name)}
                        />
                      </FormItem>
                    )}
                  </ListItem>
                ))}

                <Link
                  theme='primary'
                  prefixIcon={<AddCircleIcon style={{ marginTop: -2 }} />}
                  hover='color'
                  onClick={() => add({ host: null, port: null })}
                >
                  新增代理地址
                </Link>
              </ListContainer>
            )}
          </FormList>
        </FormItem>

        <FormItem name='note' label='代理备注' rules={[{ required: true }]}>
          <Textarea placeholder='填写备注信息' />
        </FormItem>
      </Form>
    </Dialog>
  );
}

const ListContainer = styled.div`
  width: 100%;
`;

const ListItem = styled.div`
  margin-bottom: 16px;
  display: flex;
  width: 100%;

  .t-input-number {
    width: 0;
  }
  .t-form__item {
    margin-right: 0;
    .t-input {
      border: none;
    }
    .t-input--focused {
      box-shadow: none;
    }
  }
`;

const Left = styled.div`
  flex: 1;
  display: flex;
  border-width: 1px;
  border-style: solid;
  border-radius: var(--td-radius-default);
  border-color: var(--td-border-level-2-color);
`;
